<template>
  <PinVerifyDialog
    :value="isLocked"
    :title="$t('profile.cAppSecurity.lock.title')"
    :subtitle="$t('profile.cAppSecurity.lock.subtitle')"
    :error="pinError"
    :busy="pinBusy || biometryBusy"
    :show-cancel="false"
    :show-pad="pinEnabled"
    @confirm="unlockPin"
  >
    <template v-if="biometryEnabled" #prepend>
      <v-btn
        block
        color="primary"
        :loading="biometryBusy"
        :disabled="biometryBusy"
        class="mb-4"
        @click="unlockBiometry"
      >
        <v-icon left>mdi-fingerprint</v-icon>
        {{ $t('profile.cAppSecurity.lock.biometricButton') }}
      </v-btn>
      <v-divider v-if="pinEnabled" class="mb-6" style="width: 100%" />
    </template>
  </PinVerifyDialog>
</template>

<script setup lang="ts">
import { ref, computed, watch, getCurrentInstance } from 'vue';
import { useAppLock } from '@/feature/app-lock/composables/useAppLock';
import PinVerifyDialog from './PinVerifyDialog.vue';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const vm = getCurrentInstance()!.proxy as any;

const isLocked = computed(() => vm.$store.getters['appLock/isLocked']);
const biometryEnabled = computed(() => vm.$store.getters['appLock/biometryEnabled']);
const pinEnabled = computed(() => vm.$store.getters['appLock/pinEnabled']);

const biometryBusy = ref(false);
const pinBusy = ref(false);
const pinError = ref<string | null>(null);

async function unlockBiometry() {
  if (biometryBusy.value) {
    return;
  }
  biometryBusy.value = true;
  try {
    const { unlockWithBiometry } = useAppLock();
    await unlockWithBiometry();
    vm.$store.commit('appLock/setLocked', false);
  } catch {
    // biometric failed — user can retry via button
  } finally {
    biometryBusy.value = false;
  }
}

async function unlockPin(pin: string) {
  pinBusy.value = true;
  pinError.value = null;
  try {
    const { verifyPin } = useAppLock();
    const valid = await verifyPin(pin);
    if (valid) {
      vm.$store.commit('appLock/setLocked', false);
    } else {
      pinError.value = vm.$t('profile.cAppSecurity.lock.wrongPin') as string;
    }
  } finally {
    pinBusy.value = false;
  }
}

watch(isLocked, locked => {
  if (!locked) {
    pinError.value = null;
  }
});
</script>
